/**
 * A word's full-screen view on phones (opened from the word list): its title
 * with a star, and a Play button beside its field
 */
import * as preact from "preact";

import type UserStore from "stores/UserStore";
import type UserWordStore from "stores/UserWordStore";
import * as tts from "tts";

interface Props {
  userWord: UserWordStore;
  user: UserStore;
}

interface PlayState {
  loadingAudio: boolean;
}

/** Pronounces the word, calling `done` once it starts playing, or can't */
export const playWord = (
  userWord: UserWordStore,
  user: UserStore,
  done: () => void
) =>
  tts.play({
    user,
    feature: "speakingMode",
    language: userWord.state.value.language,
    text: userWord.state.value.word,
    onStart: done,
    onEnd: done,
    onError: done,
  });

/**
 * Plays the word, beside its field in the word's full-screen view on phones,
 * where the word list's row leaves the button out
 */
export class PlayWordButton extends preact.Component<Props, PlayState> {
  state: PlayState = { loadingAudio: false };

  private play = () => {
    this.setState({ loadingAudio: true });
    void playWord(this.props.userWord, this.props.user, () =>
      this.setState({ loadingAudio: false })
    );
  };

  render() {
    const { userWord, user } = this.props;
    const supported =
      tts.getAvailabilitySignal({
        user,
        feature: "speakingMode",
        language: userWord.state.value.language,
      }).value === "supported";
    return supported ? (
      <button class="wordIconButton" title="Play" onClick={this.play}>
        <i
          class={
            this.state.loadingAudio
              ? "icon-spinner icon-spin"
              : "icon-volume-up"
          }
        ></i>
      </button>
    ) : null;
  }
}

/** The word's full-screen view's title, with its star */
export class WordSheetHeader extends preact.Component<Props> {
  private toggleStar = () => {
    void this.props.userWord.toggleFavorite();
    this.forceUpdate();
  };

  render() {
    const { word, favorite } = this.props.userWord.state.value;
    return (
      <div class="wordSheetHeader">
        <h2>{word}</h2>
        <button
          class="wordIconButton"
          title={favorite ? "Unstar" : "Star"}
          onClick={this.toggleStar}
        >
          <i class={favorite ? "icon-star" : "icon-star-empty"}></i>
        </button>
      </div>
    );
  }
}
